/*
 * PLAYXWORLD TOP-UP PAGE — SINGLE PAGE FAST FLOW WITH HISTORY & FEATURES
 * All steps visible on one page: Game → User ID → Server (if any) → Package → Confirm
 * User IDs saved in localStorage for reuse, can switch/add IDs for friends
 * Data fetched from PlusGame API via tRPC (with static fallback)
 * Includes transaction history tab and features section
 */
import Layout from "@/components/Layout";
import { Link, useParams, useLocation } from "wouter";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { PaymentCheckout } from "@/components/PaymentCheckout";
import {
  Check,
  AlertCircle,
  Loader2,
  Wallet,
  ChevronDown,
  X,
  UserPlus,
  Users,
  Trash2,
  Search,
  Gamepad2,
  Zap,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { useGameIconUrl } from "@/lib/firebaseStorage";
import { trackTopUp, trackUserAction, trackPurchaseComplete, trackError } from "@/lib/firebase-monitoring";
import { getPackagesWithCache, updatePackageCache, preloadStaticPackages } from "@/lib/package-cache";


// ==================== Types (from API) ====================

interface ApiGame {
  id: string;
  gameKey: string;
  name: string;
  image: string;
  imageDesc: string;
  description: string;
  userIdLabel: string;
  userIdHint: string;
  seq: number;
  hasServers?: boolean;
}

interface ApiProduct {
  id: string;
  gameId: string;
  name: string;
  priceKey: string;
  price: number;
  seq: number;
  rawDesc: string;
}

interface ApiServer {
  id: string;
  gameId: string;
  key: string;
  name: string;
}

// ==================== Helpers ====================

function formatPrice(price: number): string {
  return `THB ${price.toLocaleString("th-TH")}`;
}

// ==================== Saved User IDs (localStorage) ====================

interface SavedGameId {
  id: string;
  gameId: string;
  userId: string;
  serverId?: string;
  label?: string;
  lastUsed: number; // When the ID was saved
  lastUsedForTopup?: number; // When the ID was last used for a top-up
}

type SortOption = 'save-recent' | 'topup-recent';

const STORAGE_KEY = "playxworld_saved_game_ids";

function getSavedIds(gameId: string, sortBy: SortOption = 'save-recent'): SavedGameId[] {
  try {
    const all: SavedGameId[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const filtered = all.filter((s) => s.gameId === gameId);
    
    if (sortBy === 'topup-recent') {
      // Sort by top-up time first, then by save time for those without top-up
      return filtered.sort((a, b) => {
        const aTime = a.lastUsedForTopup ?? 0;
        const bTime = b.lastUsedForTopup ?? 0;
        if (aTime !== bTime) return bTime - aTime;
        return b.lastUsed - a.lastUsed;
      });
    }
    
    // Default: sort by save time
    return filtered.sort((a, b) => b.lastUsed - a.lastUsed);
  } catch {
    return [];
  }
}

function getLatestSavedId(gameId: string): SavedGameId | undefined {
  const ids = getSavedIds(gameId, 'save-recent');
  return ids.length > 0 ? ids[0] : undefined;
}

function getLatestTopupId(gameId: string): SavedGameId | undefined {
  const ids = getSavedIds(gameId, 'topup-recent');
  return ids.length > 0 && ids[0].lastUsedForTopup ? ids[0] : undefined;
}

function getSavedIdByServerKey(gameId: string, serverId: string): SavedGameId | undefined {
  try {
    const all: SavedGameId[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return all.find((s) => s.gameId === gameId && s.serverId === serverId);
  } catch {
    return undefined;
  }
}

function saveGameId(gameId: string, userId: string, serverId?: string, label?: string): SavedGameId {
  try {
    const all: SavedGameId[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const existing = all.find((s) => s.gameId === gameId && s.userId === userId && s.serverId === serverId);
    if (existing) {
      existing.lastUsed = Date.now();
      if (label) existing.label = label;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
      return existing;
    }
    const newEntry: SavedGameId = {
      id: `${gameId}-${userId}-${serverId}-${Date.now()}`,
      gameId,
      userId,
      serverId: serverId || undefined,
      label: label || undefined,
      lastUsed: Date.now(),
    };
    all.push(newEntry);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    return newEntry;
  } catch {
    return { id: `${gameId}-${userId}`, gameId, userId, serverId, label, lastUsed: Date.now() };
  }
}

function removeSavedId(id: string) {
  try {
    const all: SavedGameId[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    const filtered = all.filter((s) => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  } catch {
    // ignore
  }
}

// ==================== Game Selector Dropdown ====================

// ==================== Game Selector Icon Component ====================

function GameSelectorIcon({ game }: { game: ApiGame }) {
  const { url: firebaseIcon } = useGameIconUrl(game.gameKey);
  const imageUrl = firebaseIcon || game.image;
  
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={game.name}
        className="w-10 h-10 object-cover border-2 border-black shrink-0"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  
  return (
    <div className="w-10 h-10 bg-black/10 border-2 border-black flex items-center justify-center shrink-0">
      <Gamepad2 size={18} className="text-black/40" />
    </div>
  );
}

// ==================== Game List Item Component ====================

function GameListItem({
  game,
  isSelected,
  onSelect,
}: {
  game: ApiGame;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const { url: gameFirebaseIcon, elementRef } = useGameIconUrl(game.gameKey, true);
  const imageUrl = gameFirebaseIcon || game.image;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-black/5 transition-colors text-left ${
        isSelected ? "bg-black/10" : ""
      }`}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={game.name}
          className="w-8 h-8 object-cover border border-black/20 shrink-0"
          loading="lazy"
          decoding="async"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div className="w-8 h-8 bg-black/10 border border-black/20 flex items-center justify-center shrink-0">
          <Gamepad2 size={14} className="text-black/30" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-xs truncate">{game.name}</div>
        <div className="font-mono text-[9px] text-black/40">{game.gameKey}</div>
      </div>
      {isSelected && <Check size={14} className="text-black shrink-0" />}
    </button>
  );
}

// ==================== Game Selector ====================

function GameSelector({
  games,
  selectedGame,
  onSelect,
  isLoading,
}: {
  games: ApiGame[];
  selectedGame: ApiGame | null;
  onSelect: (game: ApiGame) => void;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = useMemo(
    () =>
      games.filter((g) =>
        g.name.toLowerCase().includes(search.toLowerCase()) ||
        g.gameKey.toLowerCase().includes(search.toLowerCase())
      ),
    [search, games]
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={isLoading}
        className="w-full flex items-center gap-3 px-4 py-3 border-3 border-black bg-white text-black hover:bg-black/5 transition-colors text-left disabled:opacity-50"
      >
        {isLoading ? (
          <div className="flex items-center gap-2 text-black/40">
            <Loader2 size={20} className="animate-spin" />
            <span className="font-heading font-semibold text-sm">{t("topup.loadingGames")}</span>
          </div>
        ) : selectedGame ? (
          <>
            <GameSelectorIcon game={selectedGame} />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm truncate">{selectedGame.name}</div>
              <div className="font-mono text-[10px] text-black/50">{selectedGame.gameKey}</div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-black/40">
            <Gamepad2 size={20} />
            <span className="font-heading font-semibold text-sm">{t("topup.selectGamePlaceholder")}</span>
          </div>
        )}
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-1 bg-white border-3 border-black z-40 max-h-80 overflow-hidden flex flex-col"
            style={{ boxShadow: "4px 4px 0px black" }}
          >
            {/* Search */}
            <div className="p-2 border-b-2 border-black/10">
              <div className="flex items-center gap-2 px-3 py-2 border-2 border-black/30">
                <Search size={14} className="text-black/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("topup.searchGames")}
                  className="flex-1 bg-transparent font-mono text-xs outline-none placeholder:text-black/30"
                  autoFocus
                />
              </div>
            </div>
            {/* Game List */}
            <div className="overflow-y-auto max-h-60">
              {filtered.map((game) => (
                <GameListItem
                  key={game.id}
                  game={game}
                  isSelected={selectedGame?.id === game.id}
                  onSelect={() => {
                    onSelect(game);
                    setOpen(false);
                    setSearch("");
                  }}
                />
              ))}

              {filtered.length === 0 && (
                <div className="px-4 py-6 text-center text-black/40 font-mono text-xs">
                  {t("topup.noGamesFound")}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==================== User ID Input with Saved IDs ====================

function UserIdInput({
  gameId,
  value,
  onChange,
  userIdLabel,
  userIdPlaceholder,
  onServerSelect,
  gameServers,
}: {
  gameId: string;
  value: string;
  onChange: (val: string) => void;
  userIdLabel?: string;
  userIdPlaceholder?: string;
  onServerSelect?: (serverId: string) => void;
  gameServers?: ApiServer[];
}) {
  const { t } = useTranslation();
  const [savedIds, setSavedIds] = useState<SavedGameId[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [labelInput, setLabelInput] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>('save-recent');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSavedIds(getSavedIds(gameId, sortBy));
  }, [gameId, sortBy]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowSaved(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSaved = (saved: SavedGameId) => {
    onChange(saved.userId);
    if (saved.serverId && onServerSelect) {
      onServerSelect(saved.serverId);
    }
    setShowSaved(false);
    saveGameId(gameId, saved.userId, saved.serverId, saved.label);
    setSavedIds(getSavedIds(gameId, sortBy));
  };



  const handleRemoveSaved = (id: string) => {
    removeSavedId(id);
    setSavedIds(getSavedIds(gameId));
  };

  return (
    <div ref={ref} className="space-y-2">
      {/* Saved IDs List — moved above input */}
      {savedIds.length > 0 && (
        <div className="relative">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setShowSaved(!showSaved)}
              className="w-full flex items-center gap-2 px-3 py-2 border-2 border-black/30 bg-black/5 text-black text-xs font-mono hover:bg-black/10 transition-colors"
            >
              <Users size={14} />
              <span>{t("topup.savedIds", { count: savedIds.length })}</span>
              <ChevronDown size={12} className={`ml-auto transition-transform ${showSaved ? "rotate-180" : ""}`} />
            </button>
            
            {/* Sort Options */}
            {showSaved && (
              <div className="flex gap-2 px-3 py-2 border-2 border-black/20 bg-black/[0.02]">
                <button
                  type="button"
                  onClick={() => setSortBy('save-recent')}
                  className={`flex-1 text-xs px-2 py-1 border-2 transition-colors ${
                    sortBy === 'save-recent'
                      ? 'border-black bg-black text-white'
                      : 'border-black/30 bg-white text-black hover:bg-black/5'
                  }`}
                >
                  {t("topup.sortBySaved")}
                </button>
                <button
                  type="button"
                  onClick={() => setSortBy('topup-recent')}
                  className={`flex-1 text-xs px-2 py-1 border-2 transition-colors ${
                    sortBy === 'topup-recent'
                      ? 'border-black bg-black text-white'
                      : 'border-black/30 bg-white text-black hover:bg-black/5'
                  }`}
                >
                  {t("topup.sortByTopup")}
                </button>
              </div>
            )}
          </div>

          <AnimatePresence>
            {showSaved && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.1 }}
                className="absolute left-0 right-0 top-full mt-1 bg-white border-2 border-black z-30 max-h-48 overflow-y-auto"
              >
                {savedIds.map((saved) => {
                  const serverName = saved.serverId && gameServers
                    ? gameServers.find(s => s.key === saved.serverId)?.name || saved.serverId
                    : undefined;
                  return (
                  <div key={saved.id} className="flex items-center gap-2 px-3 py-2 border-b-2 border-black/10 hover:bg-black/5">
                    <button
                      type="button"
                      onClick={() => handleSelectSaved(saved)}
                      className="flex-1 text-left"
                    >
                      <div className="font-mono text-xs font-bold">ID: {saved.userId}{serverName ? ` - Server: ${serverName}` : ''}</div>
                      {saved.label && <div className="font-mono text-[9px] text-black/50">{saved.label}</div>}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveSaved(saved.id)}
                      className="p-1 hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={12} className="text-red-600" />
                    </button>
                  </div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={userIdPlaceholder || t("topup.enterUserId", { label: userIdLabel || "User ID" })}
        className="w-full px-4 py-3 border-3 border-black bg-white text-black font-heading text-sm outline-none focus:bg-black/5 transition-colors"
      />
    </div>
  );
}

// ==================== Server Selector ====================

function ServerSelector({
  servers,
  value,
  onChange,
  isLoading,
}: {
  servers: ApiServer[];
  value: string;
  onChange: (val: string) => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();
  if (servers.length === 0) return null;

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={isLoading}
      className="w-full px-4 py-3 border-3 border-black bg-white text-black font-heading text-sm outline-none focus:bg-black/5 transition-colors disabled:opacity-50"
    >
      <option value="">{t("topup.selectServer")}</option>
      {servers.map((server) => (
        <option key={server.id} value={server.key}>
          {server.name}
        </option>
      ))}
    </select>
  );
}

// ==================== Package Grid ====================

function PackageGrid({
  products,
  selectedId,
  onSelect,
  isLoading,
}: {
  products: ApiProduct[];
  selectedId: string | null;
  onSelect: (pkg: ApiProduct) => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="p-4 border-3 border-black/20 bg-black/5 animate-pulse"
            style={{ boxShadow: "3px 3px 0px rgba(0,0,0,0.1)" }}
          >
            <div className="h-4 bg-black/10 rounded mb-2 w-3/4" />
            <div className="h-6 bg-black/10 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8 border-3 border-black/20 bg-black/[0.02]">
        <p className="font-mono text-sm text-black/40">{t("topup.noPackages")}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {products.map((pkg) => (
        <button
          key={pkg.id}
          type="button"
          onClick={() => onSelect(pkg)}
          className={`text-left p-4 border-3 transition-all ${
            selectedId === pkg.id
              ? "bg-black text-white border-black"
              : "bg-white text-black border-black hover:bg-black/5"
          }`}
          style={{
            boxShadow:
              selectedId === pkg.id ? "2px 2px 0px black" : "3px 3px 0px black",
            transform: selectedId === pkg.id ? "translate(1px, 1px)" : "none",
          }}
        >
          <div className="font-heading font-bold text-sm leading-tight mb-2">{pkg.name}</div>
          <div className="font-mono text-lg font-bold">{formatPrice(pkg.price)}</div>
        </button>
      ))}
    </div>
  );
}

// ==================== Main TopUp Page ====================

export default function TopUp() {
  const { t } = useTranslation();
  const params = useParams<{ gameKey?: string }>();
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();

  // Get LINE Login URL
  const { data: lineLoginData } = trpc.firebase.getLineLoginUrl.useQuery(
    { returnPath: params.gameKey ? `/topup/${params.gameKey}` : "/topup" },
    { enabled: !isAuthenticated && !authLoading }
  );

  // ---- State ----
  const [selectedGame, setSelectedGame] = useState<ApiGame | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<ApiProduct | null>(null);
  const [serverId, setServerId] = useState("");
  const [userId, setUserId] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [paymentDestRef, setPaymentDestRef] = useState("");
  const [usePointDiscount, setUsePointDiscount] = useState(false);
  const [agreeConsent, setAgreeConsent] = useState(false);

  // Refs for scrolling
  const userIdRef = useRef<HTMLDivElement>(null);
  const packageRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);

  // ---- API Queries ----

  // Fetch all games
  const { data: gamesData, isLoading: gamesLoading } = trpc.games.list.useQuery();
  const games = gamesData?.games ?? [];

  // Find game from games list by gameKey (instead of using separate getByKey query)
  const gameByKeyData = useMemo(() => {
    if (!params.gameKey || !games.length) return null;
    return games.find((g: any) => g.gameKey?.toUpperCase() === params.gameKey?.toUpperCase());
  }, [params.gameKey, games]);

  // Fetch game details (products + servers) when a game is selected
  const { data: detailsData, isLoading: detailsLoading } = trpc.game.details.useQuery(
    { gameId: selectedGame?.id ?? "" },
    { enabled: !!selectedGame }
  );

  const gameProducts = detailsData?.products ?? [];
  const gameServers = detailsData?.servers ?? [];

  // Track data version for cache invalidation
  useEffect(() => {
    if (detailsData?.dataVersion && selectedGame) {
      const cacheKey = `game-data-version-${selectedGame.id}`;
      const cachedVersion = localStorage.getItem(cacheKey);
      if (cachedVersion !== detailsData.dataVersion) {
        localStorage.setItem(cacheKey, detailsData.dataVersion);
        console.log(`[Cache] Game data version: ${detailsData.dataVersion}`);
      }
    }
  }, [detailsData?.dataVersion, selectedGame?.id]);

  // Get credit balance
  const { data: creditData } = trpc.credit.balance.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  // Get PlayXpoint balance
  const { data: playxpointData, isLoading: playxpointLoading } = trpc.playxpoint.balance.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });

  const playxpointBalance = playxpointData?.balance ?? 0;

  // Fetch mockup packages when game is selected (with caching)
  const [cachedPackages, setCachedPackages] = useState<any[] | null>(null);
  const [loadingCache, setLoadingCache] = useState(false);

  // Load from cache first
  useEffect(() => {
    if (!selectedGame) return;
    
    setLoadingCache(true);
    getPackagesWithCache(selectedGame.id).then((packages) => {
      if (packages) {
        setCachedPackages(packages);
      }
      setLoadingCache(false);
    });
  }, [selectedGame?.id]);

  // Fetch from API only if cache miss or cache is stale
  const { data: mockupPackagesData, isLoading: mockupPackagesLoading } = trpc.playxpoint.mockupPackages.useQuery(
    { gameKey: selectedGame?.gameKey ?? "" },
    { enabled: !!selectedGame && !cachedPackages } // Skip API if we have cached data
  );

  // Update cache when API data arrives
  useEffect(() => {
    if (mockupPackagesData?.packages && selectedGame) {
      updatePackageCache(selectedGame.id, mockupPackagesData.packages);
    }
  }, [mockupPackagesData, selectedGame?.id]);

  // Use cached packages first, fall back to API data
  const mockupPackages = cachedPackages ? cachedPackages : (mockupPackagesData?.packages ?? []);

  const mockupPackagesForDisplay = useMemo(() => {
    return mockupPackages.map((pkg: any) => ({
      id: pkg.id,
      gameId: selectedGame?.id ?? "",
      name: pkg.name,
      priceKey: pkg.id,
      price: pkg.price,
      seq: 0,
      rawDesc: pkg.bonus || pkg.amount,
    }));
  }, [mockupPackages, selectedGame?.id]);

  // Determine loading state (cache or API)
  const isLoadingPackages = loadingCache || mockupPackagesLoading;

  // Use mockup packages if available, otherwise use API products
  const packagesToDisplay = useMemo(() => {
    return mockupPackages.length > 0 ? mockupPackagesForDisplay : gameProducts;
  }, [mockupPackages, mockupPackagesForDisplay, gameProducts]);

  // Preload static packages on mount
  useEffect(() => {
    preloadStaticPackages();
  }, []);

  // Auto-select game from URL param (gameKey)
  useEffect(() => {
    console.log('[TopUp] Auto-select effect:', { params, gameByKeyData, selectedGame });
    if (params.gameKey && gameByKeyData && !selectedGame) {
      // Convert Firestore game data to ApiGame format
      const data = gameByKeyData as any;
      const game: ApiGame = {
        id: data.id,
        gameKey: data.gameKey,
        name: data.name,
        image: data.image,
        imageDesc: data.imageDesc || "",
        description: data.description || "",
        userIdLabel: data.userIdLabel || "User ID",
        userIdHint: data.userIdHint || "",
        seq: data.seq || 0,
        hasServers: data.hasServers,
      };
      
      console.log('[TopUp] Setting selected game:', game);
      setSelectedGame(game);
      const latestTopup = getLatestTopupId(game.id);
      const latestSave = getLatestSavedId(game.id);
      const autoFillId = latestTopup || latestSave;
      
      if (autoFillId) {
        setUserId(autoFillId.userId);
        if (autoFillId.serverId) {
          setServerId(autoFillId.serverId);
        }
      }
      
      // Scroll to top of page for mobile UX
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [params.gameKey, gameByKeyData])

  // Fallback: Auto-select game from games list if gameKey not provided
  useEffect(() => {
    if (!params.gameKey && games.length > 0 && !selectedGame) {
      // This handles the old /topup/:gameId route if still used
      // But now we primarily use /topup/:gameKey
    }
  }, [games, selectedGame]);

  // Purchase mutation
  const purchaseMutation = trpc.game.purchase.useMutation({
    onSuccess: (data) => {
      if (data.success) {
        // Show payment checkout modal instead of immediate success
        setPaymentDestRef(data.destRef as string || "");
        setShowPaymentCheckout(true);
        if (selectedGame) {
          // Track top-up usage
          const all: SavedGameId[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
          const existing = all.find((s) => s.gameId === selectedGame.id && s.userId === userId.trim() && s.serverId === (serverId || undefined));
          if (existing) {
            existing.lastUsedForTopup = Date.now();
            existing.lastUsed = Date.now();
          } else {
            const newEntry: SavedGameId = {
              id: `${selectedGame.id}-${userId.trim()}-${serverId || 'no-server'}-${Date.now()}`,
              gameId: selectedGame.id,
              userId: userId.trim(),
              serverId: serverId || undefined,
              lastUsed: Date.now(),
              lastUsedForTopup: Date.now(),
            };
            all.push(newEntry);
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
        }
        toast.success(t("topup.toastSuccess"), {
          description: `${selectedGame?.name} — ${selectedPackage?.name}`,
        });
      } else {
        if (data.error === "INSUFFICIENT_CREDIT") {
          toast.error(t("topup.toastInsufficientCredit"), {
            description: t("topup.toastInsufficientCreditDesc"),
          });
        } else {
          toast.error(data.message || t("topup.toastErrorGeneric"));
        }
      }
      setPurchasing(false);
    },
    onError: (err) => {
      toast.error(t("topup.toastErrorGeneric"), { description: err.message });
      setPurchasing(false);
    },
  });

  // ---- Handlers ----
  const handleSelectGame = useCallback(
    (game: ApiGame) => {
      setSelectedGame(game);
      setSelectedPackage(null);
      setServerId("");
      setPurchaseSuccess(false);
      // Track game selection
      trackUserAction('game_selected', {
        game_id: game.id,
        game_key: game.gameKey,
        game_name: game.name,
      });
      // Load last used ID for this game
      const saved = getSavedIds(game.id);
      if (saved.length > 0) {
        setUserId(saved[0].userId);
      } else {
        setUserId("");
      }
      // Update URL to use gameKey
      setLocation(`/topup/${game.gameKey}`, { replace: true });
      // Scroll to User ID section
      setTimeout(() => {
        userIdRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    },
    [setLocation]
  );

  const handleSelectPackage = useCallback((pkg: ApiProduct) => {
    setSelectedPackage(pkg);
    // Track package selection
    trackUserAction('package_selected', {
      package_id: pkg.id,
      package_name: pkg.name,
      price: pkg.price,
      game_id: selectedGame?.id,
    });
    setTimeout(() => {
      confirmRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [selectedGame?.id]);

  const handleConfirmPurchase = () => {
    if (!isAuthenticated) {
      toast.error(t("topup.toastLoginRequired"));
      return;
    }
    if (!selectedGame || !selectedPackage || !userId.trim()) {
      toast.error(t("topup.toastFillAllFields"));
      return;
    }
    if (gameServers.length > 0 && !serverId) {
      toast.error(t("topup.toastSelectServer"));
      return;
    }

    // Track top-up initiation
    trackTopUp(selectedGame.gameKey, selectedPackage.price, 'THB');
    trackUserAction('topup_initiated', {
      game_id: selectedGame.id,
      game_name: selectedGame.name,
      package_id: selectedPackage.id,
      package_name: selectedPackage.name,
      amount: selectedPackage.price,
      user_id_masked: userId.substring(0, 3) + '***',
      server_id: serverId || 'default',
    });

    setPurchasing(true);
    purchaseMutation.mutate({
      gameId: selectedGame.id,
      gameKey: selectedGame.gameKey,
      gameName: selectedGame.name,
      productId: selectedPackage.id,
      productName: selectedPackage.name,
      priceKey: selectedPackage.priceKey,
      price: selectedPackage.price,
      playerGameId: userId.trim(),
      serverId: serverId || undefined,
      serverKey: serverId || undefined,
    });
  };

  const handleTopUpAgain = () => {
    setSelectedPackage(null);
    setPurchaseSuccess(false);
    setTimeout(() => {
      packageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  // ---- Computed ----
  const balance = creditData?.balance ?? user?.creditBalance ?? "0.00";
  const balanceNum = parseFloat(balance);
  const canAfford = selectedPackage ? balanceNum >= selectedPackage.price : false;
  const isFormComplete =
    selectedGame &&
    userId.trim() &&
    selectedPackage &&
    (gameServers.length === 0 || serverId);

  // PlayXpoint discount calculation (10% max)
  const maxPointDiscount = selectedPackage ? Math.floor(selectedPackage.price * 0.1) : 0;
  const pointsToUse = usePointDiscount ? Math.min(maxPointDiscount, playxpointBalance) : 0;
  const discountAmount = pointsToUse;
  const finalPaymentAmount = selectedPackage ? selectedPackage.price - discountAmount : 0;

  // ==================== SUCCESS STATE ====================
  if (purchaseSuccess && selectedGame && selectedPackage) {
    return (
      <Layout>
        <section className="bg-white text-black min-h-[60vh] flex items-center justify-center py-12">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md mx-auto px-4"
          >
            <div className="card-brutal bg-white p-8 text-center">
              <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-4">
                <Check size={32} />
              </div>
              <h2 className="font-heading font-bold text-2xl mb-1">{t("topup.successTitle")}</h2>
              <p className="text-black/50 font-mono text-xs mb-6">
                {t("topup.successDescription")}
              </p>

              <div className="text-left space-y-2 border-t-2 border-black/10 pt-4 mb-6">
                <div className="flex justify-between font-mono text-xs">
                  <span className="text-black/50">{t("topup.summaryGame")}</span>
                  <span className="font-bold">{selectedGame.name}</span>
                </div>
                <div className="flex justify-between font-mono text-xs">
                  <span className="text-black/50">{t("topup.summaryPackage")}</span>
                  <span className="font-bold">{selectedPackage.name}</span>
                </div>
                <div className="flex justify-between font-mono text-xs">
                  <span className="text-black/50">{t("topup.summaryUserId")}</span>
                  <span className="font-bold">{userId}</span>
                </div>
                {serverId && (
                  <div className="flex justify-between font-mono text-xs">
                    <span className="text-black/50">{t("topup.summaryServer")}</span>
                    <span className="font-bold">
                      {gameServers.find((s) => s.key === serverId)?.name || serverId}
                    </span>
                  </div>
                )}
                <div className="flex justify-between font-heading font-bold text-base border-t-2 border-black pt-3 mt-3">
                  <span>{t("topup.summaryAmount")}</span>
                  <span>{formatPrice(selectedPackage.price)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleTopUpAgain}
                  className="btn-brutal bg-white text-black flex-1 text-center text-xs"
                >
                  {t("topup.topupAgain")}
                </button>
                <Link href="/" className="btn-brutal bg-black text-white flex-1 text-center text-xs">
                  {t("topup.backToHome")}
                </Link>
              </div>
            </div>
          </motion.div>
        </section>
      </Layout>
    );
  }

  // ==================== MAIN FLOW ====================
  return (
    <Layout>
      {/* Compact Header */}
      <section className="bg-white text-black border-b-3 border-black">
        <div className="container py-6 md:py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="font-mono text-[10px] tracking-widest text-black/50">"TOP-UP"</span>
              <div className="flex items-center gap-2">
                <Gamepad2 size={20} className="text-black" />
                <h1 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">
                  {t("topup.title")}
                </h1>
              </div>
            </div>
            {/* CREDIT box removed */}
          </div>
          
          {/* Enhanced Step Indicator — Shows all 3 steps clearly */}
          {selectedGame && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <p className="font-mono text-xs text-black/50">{t("topup.stepLabel")}</p>
              <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                {[
                  { num: 1, label: t("topup.step1"), complete: !!selectedGame },
                  { num: 2, label: t("topup.step2"), complete: userId.trim() && (gameServers.length === 0 || serverId) },
                  { num: 3, label: t("topup.step3"), complete: !!selectedPackage },
                  { num: 4, label: t("topup.step4"), complete: isFormComplete },
                ].map((step, idx) => (
                  <div key={step.num} className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center font-heading font-bold text-sm transition-all ${
                        step.complete
                          ? "bg-black text-white border-black"
                          : "border-black text-black bg-white"
                      }`}
                    >
                      {step.complete ? <Check size={18} /> : step.num}
                    </div>
                    <div className="hidden sm:block">
                      <p className="font-mono text-xs text-black/60">{step.label}</p>
                    </div>
                    {idx < 3 && <div className="hidden md:block w-6 h-px bg-black/30" />}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <section className="bg-white text-black py-6 md:py-8">
        <div className="container max-w-2xl space-y-8">
          {/* ---- STEP 1: Select Game ---- */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
                1
              </div>
              <h2 className="font-heading font-bold text-lg tracking-tight">{t("topup.step1")}</h2>
            </div>
            <GameSelector
              games={games}
              selectedGame={selectedGame}
              onSelect={handleSelectGame}
              isLoading={gamesLoading}
            />
          </div>
          
          {/* Divider */}
          {selectedGame && <div className="border-t-2 border-black/10 my-6" />}

          {/* ---- STEP 2: User ID + Server ---- */}
          {selectedGame && (
                  <div
                    ref={userIdRef}
                    className="mb-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-8 h-8 flex items-center justify-center font-heading font-bold text-sm shrink-0 ${
                          userId.trim() && (gameServers.length === 0 || serverId) ? "bg-black text-white" : "border-2 border-black text-black bg-white"
                        }`}
                      >
                        {userId.trim() && (gameServers.length === 0 || serverId) ? <Check size={18} /> : "2"}
                      </div>
                      <h2 className="font-heading font-bold text-lg tracking-tight">{t("topup.gameIdAndServer")}</h2>
                    </div>

                    <div className="space-y-4 pl-12 border-l-2 border-black/10 py-2">
                      <UserIdInput
                        gameId={selectedGame.id}
                        value={userId}
                        onChange={setUserId}
                        userIdLabel={selectedGame.userIdLabel}
                        userIdPlaceholder={selectedGame.userIdHint}
                        onServerSelect={setServerId}
                        gameServers={gameServers}
                      />

                      {/* Server — only show if game has servers */}
                      {gameServers.length > 0 && (
                        <ServerSelector
                          servers={gameServers}
                          value={serverId}
                          onChange={setServerId}
                          isLoading={detailsLoading}
                        />
                      )}

                      {/* Save ID Button — show after user selects server (or if no servers, after user enters ID) */}
                      {(() => {
                        const savedIds = getSavedIds(selectedGame.id);
                        const isDuplicate = savedIds.some(
                          (s) => s.userId === userId.trim() && s.serverId === (serverId || undefined)
                        );
                        return (
                          <AnimatePresence>
                            {userId.trim() && (gameServers.length === 0 || serverId) && !isDuplicate && (
                              <motion.button
                                key="save-id-btn"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                type="button"
                                onClick={() => {
                                  saveGameId(selectedGame.id, userId.trim(), serverId || undefined);
                                  const serverName = serverId
                                    ? gameServers.find((s) => s.key === serverId)?.name || serverId
                                    : undefined;
                                  const displayText = serverName
                                    ? `ID: ${userId.trim()} - Server: ${serverName}`
                                    : `ID: ${userId.trim()}`;
                                  toast.success(t("topup.saveIdSuccess", { label: displayText }));
                                }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-black/20 bg-black/5 text-black text-xs font-mono hover:bg-black/10 transition-colors"
                              >
                                <UserPlus size={14} />
                                <span>{t("topup.saveThisId")}</span>
                              </motion.button>
                            )}
                          </AnimatePresence>
                        );
                      })()}

                      <div className="flex items-start gap-2 p-3 border-2 border-black/15 bg-black/[0.02]">
                        <AlertCircle size={14} className="mt-0.5 shrink-0 text-black/40" />
                        <p className="text-[11px] text-black/50 leading-relaxed">
                          {t("topup.verifyUserIdWarning", { label: selectedGame.userIdLabel || "User ID" })}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

          {/* Divider */}
          {selectedGame && <div className="border-t-2 border-black/10 my-6" />
          }
          
          {/* ---- STEP 3: Select Package ---- */}
          {selectedGame && (
                  <div
                    ref={packageRef}
                    className="mb-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`w-8 h-8 flex items-center justify-center font-heading font-bold text-sm shrink-0 ${
                          selectedPackage ? "bg-black text-white" : "border-2 border-black text-black bg-white"
                        }`}
                      >
                        {selectedPackage ? <Check size={18} /> : "3"}
                      </div>
                      <h2 className="font-heading font-bold text-lg tracking-tight">{t("topup.step3")}</h2>
                    </div>

                    <div className="pl-12 border-l-2 border-black/10 py-2">
                      <PackageGrid
                        products={packagesToDisplay}
                        selectedId={selectedPackage?.id || null}
                        onSelect={handleSelectPackage}
                        isLoading={detailsLoading || mockupPackagesLoading}
                      />
                    </div>
                  </div>
                )}

          {/* Divider */}
          {selectedGame && <div className="border-t-2 border-black/10 my-6" />
          }
          
          {/* ---- STEP 4: Confirm & Pay ---- */}
          {selectedGame && (
                  <div
                    ref={confirmRef}
                    className=""
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-heading font-bold text-sm shrink-0">
                        4
                      </div>
                      <h2 className="font-heading font-bold text-lg tracking-tight">{t("topup.confirmAndPay")}</h2>
                    </div>

                    <div className="pl-12 border-l-2 border-black/10 py-2">
                      {/* Order Summary Card */}
                      <div className="border-3 border-black p-4 mb-4" style={{ boxShadow: "4px 4px 0px black" }}>
                        <div className="space-y-2">
                          <div className="flex justify-between font-mono text-xs">
                            <span className="text-black/50">{t("topup.summaryGame")}</span>
                            <span className="font-bold">{selectedGame!.name}</span>
                          </div>
                          <div className="flex justify-between font-mono text-xs">
                            <span className="text-black/50">{selectedGame!.userIdLabel || "User ID"}</span>
                            <span className="font-bold">{userId}</span>
                          </div>
                          {serverId && (
                            <div className="flex justify-between font-mono text-xs">
                              <span className="text-black/50">{t("topup.summaryServer")}</span>
                              <span className="font-bold">
                                {gameServers.find((s) => s.key === serverId)?.name || serverId}
                              </span>
                            </div>
                          )}
                          {selectedPackage && (
                            <>
                              <div className="flex justify-between font-mono text-xs">
                                <span className="text-black/50">{t("topup.summaryPackage")}</span>
                                <span className="font-bold">{selectedPackage.name}</span>
                              </div>
                              <div className="flex justify-between font-heading font-bold text-lg border-t-2 border-black pt-3 mt-2">
                                <span>{t("topup.summaryTotal")}</span>
                                <span>{formatPrice(selectedPackage.price)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* PlayXpoint Balance + Point Discount (authenticated users only) */}
                        {isAuthenticated && (
                          <>
                            <div className="flex items-center justify-between p-3 border-2 border-black/20 bg-black/[0.02]">
                              <div className="flex items-center gap-2">
                                <Wallet size={16} className="text-black/50" />
                                <span className="font-mono text-xs text-black/50">{t("topup.playxpointRemaining")}</span>
                              </div>
                              <div className="font-mono font-bold text-sm">
                                {playxpointBalance.toLocaleString("th-TH")} Points
                              </div>
                            </div>

                            {selectedPackage && (
                              <div className="border-2 border-black/20 p-3 bg-black/[0.02]">
                                <div className="flex items-start gap-3 mb-2">
                                  <input
                                    type="checkbox"
                                    id="usePointDiscount"
                                    checked={usePointDiscount}
                                    onChange={(e) => setUsePointDiscount(e.target.checked)}
                                    disabled={playxpointBalance === 0}
                                    className="mt-1 cursor-pointer"
                                  />
                                  <label htmlFor="usePointDiscount" className="cursor-pointer flex-1">
                                    <div className="font-mono text-xs font-bold">{t("topup.usePlayxpointDiscount")}</div>
                                    <div className="font-mono text-xs text-black/50 mt-1">
                                      {maxPointDiscount} Points = {formatPrice(maxPointDiscount)} THB
                                    </div>
                                  </label>
                                </div>
                                {usePointDiscount && (
                                  <div className="ml-6 space-y-1 text-xs font-mono text-black/60">
                                    <div className="flex justify-between">
                                      <span>{t("topup.originalPrice")}</span>
                                      <span>{formatPrice(selectedPackage.price)}</span>
                                    </div>
                                    <div className="flex justify-between text-green-600 font-bold">
                                      <span>{t("topup.discountLabel")}</span>
                                      <span>-{formatPrice(discountAmount)}</span>
                                    </div>
                                    <div className="flex justify-between border-t pt-1 font-bold">
                                      <span>{t("topup.finalPayment")}</span>
                                      <span>{formatPrice(finalPaymentAmount)}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        {/* Payment Method Block — ALWAYS visible (legal/policy compliance) */}
                        <div className="border-3 border-black p-6 bg-white">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="font-heading font-bold text-base text-black mb-2">PAYMENT METHOD</div>
                              <div className="flex items-center gap-3">
                                <img
                                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663229949928/ZRSsJuahteo868wQueRgPv/PromptPay-Small_8ffc32eb.png"
                                  alt="PromptPay"
                                  className="h-8 object-contain"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Accept Checkbox Block — ALWAYS visible (legal/policy compliance) */}
                        <div className="border-3 border-black p-6 bg-white">
                          <div className="flex items-start gap-4">
                            <input
                              type="checkbox"
                              id="agreeConsent"
                              checked={agreeConsent}
                              onChange={(e) => setAgreeConsent(e.target.checked)}
                              className="mt-1 cursor-pointer w-6 h-6 accent-black"
                            />
                            <label htmlFor="agreeConsent" className="cursor-pointer flex-1 text-sm leading-relaxed">
                              <span className="text-black font-semibold">I HAVE READ AND AGREE TO THE </span>
                              <Link href="/terms" className="text-black underline font-bold hover:opacity-70">USER AGREEMENT</Link>
                              <span className="text-black font-semibold"> AND </span>
                              <Link href="/privacy" className="text-black underline font-bold hover:opacity-70">PRIVACY POLICY</Link>
                              <span className="text-black font-semibold"> AND </span>
                              <Link href="/refund" className="text-black underline font-bold hover:opacity-70">REFUND POLICY</Link>
                            </label>
                          </div>
                        </div>

                        {/* LINE LOGIN Block — shown when not authenticated */}
                        {!isAuthenticated && !authLoading && (
                          <div className="p-4 border-3 border-black bg-black/5">
                            <p className="font-mono text-xs text-black/60 mb-3">
                              {t("topup.loginToPay")}
                            </p>
                            {agreeConsent ? (
                              <a
                                href={lineLoginData?.url || "#"}
                                className="btn-brutal bg-[#06C755] text-white w-full text-center inline-flex items-center justify-center gap-2 border-[#06C755] py-3 font-heading font-bold"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                                </svg>
                                {t("topup.lineLoginButton")}
                              </a>
                            ) : (
                              <button
                                disabled
                                className="btn-brutal bg-gray-300 text-black w-full text-center py-3 opacity-50 cursor-not-allowed font-heading font-bold"
                              >
                                ✓ {t("topup.lineLoginButton")}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Payment Button — only when authenticated and agreed */}
                        {isAuthenticated && agreeConsent && selectedPackage && (
                          <button
                            onClick={handleConfirmPurchase}
                            disabled={purchasing || !canAfford || !isFormComplete}
                            className="btn-brutal bg-black text-white w-full text-center disabled:opacity-40 disabled:cursor-not-allowed font-heading font-bold py-3"
                          >
                            {purchasing ? (
                              <span className="flex items-center justify-center gap-2">
                                <Loader2 size={16} className="animate-spin" />
                                {t("topup.processing")}
                              </span>
                            ) : (
                              t("topup.paySimple", { amount: formatPrice(usePointDiscount ? finalPaymentAmount : selectedPackage.price) })
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

          {/* Empty state when no game selected */}
          {!selectedGame && !gamesLoading && (
                <div className="text-center py-12">
                  <Gamepad2 size={48} className="mx-auto mb-4 text-black/15" />
                  <p className="font-mono text-sm text-black/40">{t("topup.selectGameToStart")}</p>
                </div>
              )}

        </div>
      </section>

      {/* Features Section */}
      <section className="bg-black text-white py-12 md:py-16 border-t-3 border-black">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="border-3 border-white p-6" style={{ boxShadow: "3px 3px 0px white" }}>
              <Zap size={32} className="mb-3" />
              <h3 className="font-heading font-bold text-lg mb-2">{t("topup.featureFastTitle")}</h3>
              <p className="text-white/70 text-sm">{t("topup.featureFastDesc")}</p>
            </div>
            <div className="border-3 border-white p-6" style={{ boxShadow: "3px 3px 0px white" }}>
              <Shield size={32} className="mb-3" />
              <h3 className="font-heading font-bold text-lg mb-2">{t("topup.featureSecureTitle")}</h3>
              <p className="text-white/70 text-sm">{t("topup.featureSecureDesc")}</p>
            </div>
            <div className="border-3 border-white p-6" style={{ boxShadow: "3px 3px 0px white" }}>
              <Check size={32} className="mb-3" />
              <h3 className="font-heading font-bold text-lg mb-2">{t("topup.featureVerifiedTitle")}</h3>
              <p className="text-white/70 text-sm">{t("topup.featureVerifiedDesc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Checkout Modal */}
      <PaymentCheckout
        isOpen={showPaymentCheckout}
        onClose={() => setShowPaymentCheckout(false)}
        destRef={paymentDestRef}
        amount={selectedPackage?.price || 0}
        gameName={selectedGame?.name || ""}
        packageName={selectedPackage?.name || ""}
        onPaymentSuccess={() => {
          setPurchaseSuccess(true);
          setShowPaymentCheckout(false);
        }}
      />
    </Layout>
  );
}
